"use client";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

import Language from "./LanguageDropdown";
import logo from "../logo.svg";
import { dataFR } from "../assets/data/fr";
import { dataEN } from "../assets/data/en";
import { dataNL } from "../assets/data/nl";

function Navbar() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  const [isScrolled, setIsScrolled] = useState(false);

  let data = dataFR;
  if (currentLanguage === "fr") {
    data = dataFR;
  } else if (currentLanguage === "en") {
    data = dataEN;
  } else if (currentLanguage === "nl") {
    data = dataNL;
  }

  const navigation = data.navigation.filter((item) => item.href !== "/notification");
  const uiLabels = {
    fr: {
      menu: "Menu",
      language: "Langue",
      openMenu: "Ouvrir le menu",
      closeMenu: "Fermer le menu",
    },
    en: {
      menu: "Menu",
      language: "Language",
      openMenu: "Open menu",
      closeMenu: "Close menu",
    },
    nl: {
      menu: "Menu",
      language: "Taal",
      openMenu: "Menu openen",
      closeMenu: "Menu sluiten",
    },
  }[currentLanguage] || {
    menu: "Menu",
    language: "Langue",
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
  };

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1000);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1000;
      setIsMobile(mobile);

      if (!mobile) {
        setShowModal(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setShowModal(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!showModal) {
      document.body.style.overflow = "";
      return undefined;
    }

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowModal(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [showModal]);

  const toggleModal = () => {
    setShowModal((current) => !current);
  };

  const handleNavigationClick = () => {
    setShowModal(false);
  };

  const isCurrentPage = (href) => location.pathname === href;

  return (
    <>
      {!isMobile ? (
        <div
          className={`sticky top-0 z-40 w-full transition-all duration-300 ${
            isScrolled
              ? "border-b border-[#43A047]/10 bg-white/90 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur-xl"
              : "bg-white/72 backdrop-blur-md"
          }`}
        >
          <nav className="container relative mx-auto flex flex-wrap items-center justify-between px-8 py-5 lg:justify-between xl:px-1">
            <Link to="/" className="shrink-0">
              <span className="flex items-center space-x-3 text-2xl font-medium text-green-600 dark:text-gray-100">
                <span>
                  <img
                    src={logo}
                    width="32"
                    alt="Pharma Manufacturing Logo"
                    height="32"
                    className="w-8"
                    loading="lazy"
                  />
                </span>
                <span className="text-lg font-bold uppercase tracking-[0.18em] text-[#2f7d32]">
                  Pharma Manufacturing
                </span>
              </span>
            </Link>
            <div className="ml-10 flex flex-1 items-center justify-end gap-5">
            <ul className="flex cursor-pointer items-center justify-end gap-1 rounded-full bg-[#43A047]/5 px-3 py-2">
              {navigation.map((menu, index) => (
                <li key={index}>
                <Link
                  to={menu.href}
                  aria-current={isCurrentPage(menu.href) ? "page" : undefined}
                  className={`inline-flex items-center rounded-full px-4 py-2 text-[0.95rem] font-medium no-underline transition-all duration-200 focus:outline-none ${
                    isCurrentPage(menu.href)
                      ? "bg-white text-[#2f7d32] shadow-[0_8px_18px_rgba(67,160,71,0.15)]"
                      : "text-gray-700 hover:bg-white/80 hover:text-[#43A047]"
                  }`}
                >
                  {menu.label}
                </Link>
                </li>
              ))}
            </ul>
            <Language
              className="lg:order-3"
              buttonClassName="rounded-full bg-[#43A047]/7 px-4 py-2 text-sm font-semibold text-[#2f7d32] hover:bg-[#43A047]/12"
              menuClassName="right-0 mt-3 rounded-2xl border border-[#43A047]/10 bg-white p-1.5 shadow-[0_18px_45px_rgba(15,23,42,0.12)] ring-0"
              itemClassName="rounded-xl px-4 py-3 text-slate-700"
            />
            </div>
          </nav>
        </div>
      ) : (
        <nav className="px-4 py-5 sm:px-6">
          <div className="mx-auto flex items-center justify-between gap-4 rounded-full bg-white/95 px-4 py-3 backdrop-blur-sm">
            <Link to="/" onClick={handleNavigationClick}>
              <div className="flex items-center gap-3">
                <img
                  src={logo}
                  width="32"
                  height="32"
                  alt="Pharma Manufacturing Logo"
                  className="h-8 w-8"
                  loading="lazy"
                />
                <div className="max-w-[12rem] truncate text-sm font-bold uppercase tracking-[0.18em] text-[#2f7d32]">
                  Pharma Manufacturing
                </div>
              </div>
            </Link>

            <button
              type="button"
              onClick={toggleModal}
              aria-expanded={showModal}
              aria-label={
                showModal ? uiLabels.closeMenu : uiLabels.openMenu
              }
              className="inline-flex items-center gap-2 rounded-full bg-[#43A047]/10 px-4 py-2 text-sm font-semibold text-[#2f7d32] transition-all duration-200 hover:bg-[#43A047]/16 active:scale-95"
            >
              <span>{uiLabels.menu}</span>
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>

          <div
            className={`fixed inset-0 z-50 transition-opacity duration-300 ${
              showModal
                ? "pointer-events-auto opacity-100"
                : "pointer-events-none opacity-0"
            }`}
            aria-hidden={!showModal}
          >
            <button
              type="button"
              aria-label={uiLabels.closeMenu}
              className="absolute inset-0 bg-slate-950/30 backdrop-blur-[2px]"
              onClick={toggleModal}
            />

            <div
              className={`absolute right-0 top-0 flex h-full w-full max-w-sm transform flex-col overflow-y-auto overscroll-contain bg-[#eef7ee] shadow-2xl transition-transform duration-300 ease-out ${
                showModal ? "translate-x-0" : "translate-x-full"
              }`}
            >
              <div className="rounded-bl-[2rem] bg-[#43A047] px-6 pb-8 pt-6 text-white">
                <div className="flex items-start justify-between gap-4">
                  <Link to="/" onClick={handleNavigationClick}>
                    <div className="flex items-center gap-3">
                      <img
                        src={logo}
                        width="40"
                        height="40"
                        alt="Pharma Manufacturing Logo"
                        className="h-10 w-10 rounded-full bg-white p-1.5"
                        loading="lazy"
                      />
                      <div className="max-w-[12rem] text-left text-base font-bold uppercase leading-[1.1] tracking-[0.16em] text-white">
                        Pharma Manufacturing
                      </div>
                    </div>
                  </Link>

                  <button
                    type="button"
                    onClick={toggleModal}
                    aria-label={uiLabels.closeMenu}
                    className="rounded-full bg-white/16 p-2 text-white transition-colors hover:bg-white/24 active:scale-95"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="mt-10 text-left">
                  <div className="text-[0.95rem] font-semibold uppercase tracking-[0.32em] text-white/95">
                    {uiLabels.menu}
                  </div>
                </div>
              </div>

              <div className="flex flex-1 flex-col gap-6 px-5 py-6">
                <div className="flex flex-col gap-3">
                    {navigation.map((menu, index) => {
                      const isActive = isCurrentPage(menu.href);

                      return (
                      <Link
                        to={menu.href}
                        key={index}
                        onClick={handleNavigationClick}
                        aria-current={isActive ? "page" : undefined}
                        className={`block rounded-2xl px-5 py-4 text-left text-lg font-semibold no-underline transition-all duration-200 active:scale-[0.99] ${
                          isActive
                            ? "bg-white text-[#2f7d32]"
                            : "bg-[#43A047]/8 text-slate-800 hover:bg-white/80"
                        }`}
                      >
                        <span>{menu.label}</span>
                      </Link>
                    );
                  })}
                </div>

                <div className="mt-auto pt-2">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                    {uiLabels.language}
                  </div>
                  <Language
                    className="block w-full"
                    buttonClassName="w-full justify-between rounded-2xl bg-white px-4 py-3 text-base text-[#2f7d32]"
                    menuClassName="left-0 right-0 mt-3 w-full rounded-2xl bg-white p-1 ring-0"
                    itemClassName="rounded-xl px-4 py-3 text-slate-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}
    </>
  );
}

export default Navbar;
